import { Grid, Spin } from "antd";
import { useEffect, useState } from "react";
import {
  ActiveUsers,
  Bunker,
  FullSwing,
  InActiveUser,
  Pitching,
  Batter,
  Putting,
  ShortGame,
  TotalScans,
  TotalSwings,
  Chart,
} from "./components";
import {
  TotalSwingIcon,
  TotalSwingBaseballIcon,
} from "pages/internal-dashboard/icons";
import { ConditionalRenderer } from "components";
import {
  calculateContainerProperties,
  getPercentagesBasedOnKey,
} from "./helpers";
import { sportnames, userActivityKeys } from "./constants";
import { useUserActivityData } from "./useUserActivityData";
import styles from "./styles.module.scss";

const { useBreakpoint } = Grid;

export function UserActivity({
  activeSportType,
  activePlateform,
  activeFeature,
}: any) {
  const screens = useBreakpoint();
  const { fullswing, scan, shortgame, putter, bunker, captures } =
    userActivityKeys;
  const { golf, baseball } = sportnames;
  const [gridProperties, setGridProperties] = useState({
    col: 0,
    colWidth: 0,
    gap: 0,
    width: 0,
  });

  const { golfUserActivity, baseballUserActivity } = useUserActivityData({
    activeSportType,
    activeFeature,
    activePlateform,
  });

  useEffect(() => {
    function handleGridContainer() {
      const { width = 0 }: Partial<DOMRect> =
        document.getElementById("grid-container")?.getBoundingClientRect() ||
        {};

      setGridProperties(calculateContainerProperties({ width, screens }));
    }

    handleGridContainer();
    window.addEventListener("resize", handleGridContainer);
    return () => window.removeEventListener("resize", handleGridContainer);
  }, [screens]);

  const {
    data,
    isLoading,
    usersActiveCount,
    usersActiveLoading,
    usersNonActiveCount,
    usersNonActiveLoading,
    scanData,
    scanLoading,
  } = golfUserActivity || {};

  const { baseBallAnalysisData, baseBallAnalysisLoading } =
    baseballUserActivity || {};

  // (containerwidth - gap * no. of col - 1)/ no. of col

  const marginBottom = screens.xxl ? 30 : 15;
  const marginTop = screens.xxl ? 20 : 10;

  return (
    <div className={styles.userActivityContainer}>
      <div
        className={styles.gridConatainer1}
        id="grid-container"
        style={{
          gridTemplateColumns: `repeat(${gridProperties?.col}, ${gridProperties?.colWidth}px)`,
          gap: gridProperties?.gap + "px",
          marginBottom: marginBottom + "px",
          marginTop: marginTop + "px",
        }}
      >
        {gridProperties?.width ? (
          <>
            <ConditionalRenderer condition={activeSportType === golf}>
              <ActiveUsers
                data={usersActiveCount?.data}
                loading={usersActiveLoading}
              />
              <InActiveUser
                data={usersNonActiveCount?.data}
                loading={usersNonActiveLoading}
              />
              <TotalSwings
                data={data?.result?.captures}
                loading={isLoading}
                percentages={getPercentagesBasedOnKey(data, captures)}
                title="User Swing Count"
                icon={TotalSwingIcon}
              />
              <Chart />
            </ConditionalRenderer>
            <ConditionalRenderer condition={activeSportType === baseball}>
              <ActiveUsers data={{}} />
              <InActiveUser />
              <TotalSwings
                data={{}}
                loading={false}
                percentages={{}}
                title="Total Swing Count"
                icon={TotalSwingBaseballIcon}
              />
              <Chart />
            </ConditionalRenderer>
          </>
        ) : (
          <div className={styles.loading}>
            <Spin />
          </div>
        )}
      </div>
      <div
        className={styles.gridConatainer2}
        style={{
          gridTemplateColumns: `repeat(${gridProperties?.col}, ${gridProperties?.colWidth}px)`,
          gap: gridProperties?.gap + "px",
        }}
      >
        <ConditionalRenderer condition={gridProperties?.width}>
          <ConditionalRenderer condition={activeSportType === baseball}>
            <Pitching
              data={baseBallAnalysisData?.data?.pitching}
              loading={baseBallAnalysisLoading}
              percentages={{}}
            />
            <Batter
              data={baseBallAnalysisData?.data?.batter}
              loading={baseBallAnalysisLoading}
              percentages={{}}
            />
          </ConditionalRenderer>
          <ConditionalRenderer condition={activeSportType === golf}>
            <TotalScans
              data={activeFeature === "3d" ? scanData?.result?.scan : {}}
              loading={scanLoading}
              percentages={
                activeFeature === "3d"
                  ? getPercentagesBasedOnKey(scanData, scan)
                  : getPercentagesBasedOnKey(undefined, undefined)
              }
            />
            <FullSwing
              data={data?.result?.fullswing}
              loading={isLoading}
              percentages={getPercentagesBasedOnKey(data, fullswing)}
            />
            <Putting
              data={data?.result?.putter}
              loading={isLoading}
              percentages={getPercentagesBasedOnKey(data, putter)}
            />

            <Bunker
              data={data?.result?.bunker}
              loading={isLoading}
              percentages={getPercentagesBasedOnKey(data, bunker)}
            />

            <ShortGame
              data={data?.result?.shortgame}
              loading={isLoading}
              percentages={getPercentagesBasedOnKey(data, shortgame)}
            />
          </ConditionalRenderer>
        </ConditionalRenderer>
      </div>
    </div>
  );
}
